import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { RootState } from 'store/store';
import { complaintValidationStatuses } from 'types/ComplaintTracking';
import { TRANSLATION_KEYS } from 'utils/constants';

export const useComplaintHook = () => {
	const { t: tDbLabels } = useTranslation('', {
		keyPrefix: TRANSLATION_KEYS.DB_LABELS,
	});

	const { complaintDetail } = useSelector(
		(store: RootState) => store.complaintTracking,
	);

	const { complaintStatuses, complaintTypes } = useSelector(
		(store: RootState) => store.form,
	);

	const [errors, setErrors] = useState<string[]>([]);

	const isClosureDateMandatory = useMemo(() => {
		return (
			complaintDetail?.status ===
			complaintStatuses.find(
				status => status.code === complaintValidationStatuses.RESOLVED,
			)?.code
		);
	}, [complaintStatuses, complaintDetail?.status]);

	const isFRLsentMandatory = useMemo(() => {
		return (
			complaintDetail?.type ===
				complaintTypes.find(
					type => type.code === complaintValidationStatuses.FORMAL,
				)?.code && !!complaintDetail?.closureDate
		);
	}, [complaintTypes, complaintDetail?.closureDate, complaintDetail?.type]);

	const isFRLSentDateMandatory = useMemo(() => {
		return complaintDetail?.frlSent === complaintValidationStatuses.FRLSENT_YES;
	}, [complaintDetail?.frlSent]);

	const isEditEnabled = useMemo(() => {
		const editableCodes = [
			complaintValidationStatuses.RESOLVED,
			complaintValidationStatuses.CANCELED_MEMBER_REQUEST,
			complaintValidationStatuses.CANCELED_ENTERED_IN_ERROR,
		];

		return (
			!!complaintDetail?.status && editableCodes.includes(complaintDetail.status)
		);
	}, [complaintDetail?.status]);

	const validateForm = useCallback(() => {
		let errors = [];

		if (!complaintDetail?.source) {
			errors.push('source');
		}

		if (!complaintDetail?.type) {
			errors.push('type');
		}

		if (!complaintDetail?.status) {
			errors.push('status');
		}

		if (!complaintDetail?.requestedDate) {
			errors.push('requestedDate');
		}

		if (isClosureDateMandatory && !complaintDetail?.closureDate) {
			errors.push('closureDate');
		}

		if (isFRLsentMandatory && complaintDetail?.frlSent === undefined) {
			errors.push('frlSent');
		}

		if (isFRLSentDateMandatory && !complaintDetail?.frlSentDate) {
			errors.push('frlSentDate');
		}

		setErrors(errors);

		return errors.length === 0;
	}, [
		complaintDetail?.closureDate,
		complaintDetail?.frlSent,
		complaintDetail?.frlSentDate,
		complaintDetail?.requestedDate,
		complaintDetail?.source,
		complaintDetail?.status,
		complaintDetail?.type,
		isClosureDateMandatory,
		isFRLSentDateMandatory,
		isFRLsentMandatory,
	]);

	const formatFRLSent = (value: boolean | undefined) => {
		if (value === true) {
			return tDbLabels('complaint_FRLSent.yes');
		}
		if (value === false) {
			return tDbLabels('complaint_FRLSent.no');
		}

		return '-';
	};

	return {
		isClosureDateMandatory,
		isFRLsentMandatory,
		isFRLSentDateMandatory,
		validateForm,
		errors,
		isEditEnabled,
		formatFRLSent,
	};
};
